/**
 * @file Contains functions dedicated to rendering Kanban board layouts
 * for different item types (Tasks, Patients, Projects, Assets) and different
 * grouping strategies (by Status, by Date). Includes both standard ("fast")
 * and detailed ("WIP", "slow") rendering modes.
 */

// --- Imports ---

// State management
import { state, appData } from '../../app/state/state.js';
import { applyBoardRuntimePipeline } from './boardRuntime.js';
import { renderBoardStateCard, renderGroupedKanbanBoard } from './boardKanbanView.js';
// Utility functions
import {
    getSortedCategories,
    getKanbanSortDate,
    getAssetDisplayDate,
} from "../utils/itemLookup.js";
import {
    getGridColsClass,
} from "../utils/displayUtils.js";
import {
    isItemClosed,
} from "../utils/closedState.js";
import {
    getWeekBoundaries,
    getMonthBoundaries,
} from "../utils/dateUtils.js";
// Card rendering functions
import { createTaskCard, createPatientCard_Fast, createProjectCard_Fast, createAssetCard_Fast } from './parentBoardCards.js';
import { loadMoreClosedItems as saveLoadMoreClosedItems } from '../api/parentItems.js';

async function loadMoreClosedItems(collectionType) {
    await saveLoadMoreClosedItems(collectionType);
}

/**
 * Renders the configured task kanban board.
 * @param {HTMLElement} board Board container.
 * @param {Array<object>} tasks Task records for the current view.
 */
export function renderTaskKanban(board, tasks) {
    const settings = state.viewSettings?.tasks;
    if (!settings) {
        console.error("renderTaskKanban: Task view settings not loaded.");
        if (board) {
            board.className = 'grid grid-cols-1 gap-6';
            board.innerHTML = renderBoardStateCard({
                title: 'Board unavailable',
                body: 'The board settings could not be loaded.',
                tone: 'error',
            });
        }
        return;
    }

    renderGroupedKanbanBoard(board, {
        viewKey: 'tasks',
        itemType: 'task',
        singularLabel: 'task',
        sourceItems: tasks || [],
        items: applyBoardRuntimePipeline({
            viewKey: 'tasks',
            itemType: 'task',
            items: tasks || [],
            taskList: appData.tasks,
        }),
        groupBy: settings.boardGroupBy,
        dateGroupingMode: settings.boardDateGroupingMode,
        taskList: appData.tasks,
        renderCard: (task) => createTaskCard(task),
        onLoadMoreClosed: loadMoreClosedItems,
    });
}

/**
 * Renders the configured patient kanban board.
 * @param {HTMLElement} board Board container.
 */
export function renderPatientKanban(board) {
    const settings = state.viewSettings?.patients;
    if (!settings) {
        console.error("renderPatientKanban: Patient view settings not loaded.");
        if (board) {
            board.className = 'grid grid-cols-1 gap-6';
            board.innerHTML = renderBoardStateCard({
                title: 'Board unavailable',
                body: 'The board settings could not be loaded.',
                tone: 'error',
            });
        }
        return;
    }

    renderGroupedKanbanBoard(board, {
        viewKey: 'patients',
        itemType: 'patient',
        singularLabel: 'patient',
        sourceItems: appData?.patients || [],
        items: applyBoardRuntimePipeline({
            viewKey: 'patients',
            itemType: 'patient',
            items: appData?.patients || [],
            taskList: appData.tasks,
        }),
        groupBy: settings.boardGroupBy,
        dateGroupingMode: settings.boardDateGroupingMode,
        taskList: appData.tasks,
        renderCard: (patient) => createPatientCard_Fast(patient),
        onLoadMoreClosed: loadMoreClosedItems,
    });
}

/**
 * Renders the configured project kanban board.
 * @param {HTMLElement} board Board container.
 */
export function renderProjectKanban(board) {
    const settings = state.viewSettings?.projects;
    if (!settings) {
        console.error("renderProjectKanban: Project view settings not loaded.");
        if (board) {
            board.className = 'grid grid-cols-1 gap-6';
            board.innerHTML = renderBoardStateCard({
                title: 'Board unavailable',
                body: 'The board settings could not be loaded.',
                tone: 'error',
            });
        }
        return;
    }

    renderGroupedKanbanBoard(board, {
        viewKey: 'projects',
        itemType: 'project',
        singularLabel: 'project',
        sourceItems: appData?.projects || [],
        items: applyBoardRuntimePipeline({
            viewKey: 'projects',
            itemType: 'project',
            items: appData?.projects || [],
            taskList: appData.tasks,
        }),
        groupBy: settings.boardGroupBy,
        dateGroupingMode: settings.boardDateGroupingMode,
        taskList: appData.tasks,
        renderCard: (project) => createProjectCard_Fast(project),
        onLoadMoreClosed: loadMoreClosedItems,
    });
}

/**
 * Renders the configured asset kanban board.
 * @param {HTMLElement} board Board container.
 */
export function renderAssetKanban(board) {
    const settings = state.viewSettings?.assets;
    if (!settings) {
        console.error("renderAssetKanban: Asset view settings not loaded.");
        if (board) {
            board.className = 'grid grid-cols-1 gap-6';
            board.innerHTML = renderBoardStateCard({
                title: 'Board unavailable',
                body: 'The board settings could not be loaded.',
                tone: 'error',
            });
        }
        return;
    }

    renderGroupedKanbanBoard(board, {
        viewKey: 'assets',
        itemType: 'asset',
        singularLabel: 'asset',
        sourceItems: appData?.assets || [],
        items: applyBoardRuntimePipeline({
            viewKey: 'assets',
            itemType: 'asset',
            items: appData?.assets || [],
            taskList: appData.tasks,
        }),
        groupBy: settings.boardGroupBy,
        dateGroupingMode: settings.boardDateGroupingMode,
        taskList: appData.tasks,
        renderCard: (asset) => createAssetCard_Fast(asset),
        onLoadMoreClosed: loadMoreClosedItems,
    });
}

// --- Task Kanban Renderers ---

/**
 * Renders the Task Kanban board grouped and sorted by due date categories
 * (Overdue, Today, Tomorrow, Later).
 * @param {HTMLElement} board - The container element for the Kanban board.
 * @param {Array<object>} tasks - The list of task objects to render (usually filtered for the current user).
 */
export function renderTaskKanbanByDate(board, tasks) {
    if (!board) { console.error("renderTaskKanbanByDate: Board element not provided."); return; }
    if (!tasks) { console.error("renderTaskKanbanByDate: Tasks array not provided."); return; }

    const showClosed = state.showClosed || false;
    
    // --- FIX: Use getGridColsClass for responsive layout ---
    const numCols = showClosed ? 5 : 4;
    const gridClass = getGridColsClass(numCols);
    board.className = `grid grid-cols-1 ${gridClass} gap-6`;
    board.innerHTML = ''; // Clear previous content

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isSameDay = (d1, d2) => d1 && d2 && d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

    const taskColumns = {
        overdue: { title: 'Overdue', tasks: [] },
        today: { title: 'Due Today', tasks: [] },
        tomorrow: { title: 'Due Tomorrow', tasks: [] },
        later: { title: 'Due Later', tasks: [] },
        closed: { title: 'Closed', tasks: [] }
    };

    tasks.forEach(task => {
        if (isItemClosed(task)) {
            if (showClosed) {taskColumns.closed.tasks.push(task);}
            return;
        }

        if (!task.due_date) {
            taskColumns.later.tasks.push(task);
            return;
        }
        try {
            const dueDate = new Date(task.due_date);
            if (isNaN(dueDate.getTime())) {
                console.warn(`Invalid due date for task ${task.id}: ${task.due_date}`);
                taskColumns.later.tasks.push(task);
                return;
            }

            if (dueDate < today) {
                taskColumns.overdue.tasks.push(task);
            } else if (isSameDay(dueDate, today)) {
                taskColumns.today.tasks.push(task);
            } else if (isSameDay(dueDate, tomorrow)) {
                taskColumns.tomorrow.tasks.push(task);
            } else {
                taskColumns.later.tasks.push(task);
            }
        } catch (e) {
             console.error(`Error processing due date for task ${task.id} ('${task.due_date}'):`, e);
             taskColumns.later.tasks.push(task);
        }
    });

    // --- Sort tasks within each column ---
    const priorities = appData?.config?.priorities || [];
    const taskStatuses = appData?.config?.taskStatus || [];
    const priorityOrderMap = new Map(priorities.map(p => [p.id, parseInt(p.order, 10) || 0]));
    const statusOrderMap = new Map(taskStatuses.map((s, index) => [s.id, s.order ?? index]));
    const getPriorityOrder = (id) => priorityOrderMap.get(id) ?? 999;
    const getStatusOrder = (id) => statusOrderMap.get(id) ?? 999;

    Object.values(taskColumns).forEach(col => {
        col.tasks.sort((a, b) => {
            const priorityOrderA = getPriorityOrder(a.priority);
            const priorityOrderB = getPriorityOrder(b.priority);
            if (priorityOrderA !== priorityOrderB) {return priorityOrderA - priorityOrderB;}

            const statusOrderA = getStatusOrder(a.status);
            const statusOrderB = getStatusOrder(b.status);
            if (statusOrderA !== statusOrderB) {return statusOrderA - statusOrderB;}

            const dateA = a.due_date ? new Date(a.due_date) : null;
            const dateB = b.due_date ? new Date(b.due_date) : null;
            if (!dateA && !dateB) {return 0;}
            if (!dateA) {return 1;}
            if (!dateB) {return -1;}
            return dateA - dateB;
        });
    });

    // --- Generate HTML ---
    const columnsToRender = Object.values(taskColumns).filter(col => {
        return col.title !== 'Closed' || (col.title === 'Closed' && showClosed);
    });

    board.innerHTML = columnsToRender.map(col => `
        <div class="bg-white p-4 rounded-lg shadow kanban-column">
            <h3 class="font-bold text-lg mb-4 text-gray-800">${col.title} <span class="text-sm font-normal text-gray-500">${col.tasks.length}</span></h3>
            <div class="space-y-3">
                ${col.tasks.map(task => createTaskCard(task)).join('')}
            </div>
        </div>`).join('');
}

/**
 * Renders the Task Kanban board grouped by status category.
 * (This function was already correct)
 */
export function renderTaskKanbanByStatus(board, tasks) {
    if (!board) { console.error("renderTaskKanbanByStatus: Board element not provided."); return; }
    if (!tasks) { console.error("renderTaskKanbanByStatus: Tasks array not provided."); return; }

    board.innerHTML = ''; // Clear previous content

    const showClosed = state.showClosed || false;
    let categories = getSortedCategories('task'); // from utils.js
    if (!showClosed) {
        categories = categories.filter(c => c.isClosed !== true);
    }

    const taskStatusList = appData?.config?.taskStatus || [];
    const tasksByCategory = {};
    categories.forEach(cat => tasksByCategory[cat.id] = []);
    tasksByCategory['uncategorised'] = [];

    const tasksToDisplay = showClosed ? tasks : tasks.filter(task => !isItemClosed(task));

    tasksToDisplay.forEach(task => {
        const statusConfig = taskStatusList.find(s => s.id === task.status);
        const categoryId = statusConfig?.category;

        if (categoryId && tasksByCategory[categoryId]) {
            tasksByCategory[categoryId].push(task);
        } else {
            tasksByCategory['uncategorised'].push(task);
        }
    });

    // --- Sort tasks within each category ---
    const priorities = appData?.config?.priorities || [];
    const priorityOrderMap = new Map(priorities.map(p => [p.id, parseInt(p.order, 10) || 0]));
    const statusOrderMap = new Map(taskStatusList.map((s, index) => [s.id, s.order ?? index]));
    const getPriorityOrder = (id) => priorityOrderMap.get(id) ?? 999;
    const getStatusOrder = (id) => statusOrderMap.get(id) ?? 999;

    Object.values(tasksByCategory).forEach(taskArray => {
        taskArray.sort((a, b) => {
            const priorityOrderA = getPriorityOrder(a.priority);
            const priorityOrderB = getPriorityOrder(b.priority);
            if (priorityOrderA !== priorityOrderB) {return priorityOrderA - priorityOrderB;}

            const statusOrderA = getStatusOrder(a.status);
            const statusOrderB = getStatusOrder(b.status);
            if (statusOrderA !== statusOrderB) {return statusOrderA - statusOrderB;}

            const dateA = a.due_date ? new Date(a.due_date) : null;
            const dateB = b.due_date ? new Date(b.due_date) : null;
            if (!dateA && !dateB) {return 0;}
            if (!dateA) {return 1;}
            if (!dateB) {return -1;}
            return dateA - dateB;
        });
    });

    // --- Generate HTML ---
    let html = categories.map(category => {
        const categoryTasks = tasksByCategory[category.id] || [];
        const headerColor = category.color || '#cccccc';

        return `
        <div class="bg-white p-4 rounded-lg shadow kanban-column" data-column-id="${category.id}">
            <h3 class="font-bold text-lg mb-4 text-gray-800 flex items-center capitalize">
                <span class="w-3 h-3 rounded-full mr-2" style="background-color: ${headerColor};"></span>
                ${category.name || 'Unknown Category'}
                <span class="text-sm font-normal text-gray-500 ml-2">${categoryTasks.length}</span>
            </h3>
            <div class="space-y-3">
                ${categoryTasks.map(task => createTaskCard(task)).join('')}
            </div>
        </div>`;
    }).join('');

    const uncategorisedTasks = tasksByCategory['uncategorised'];
    if (uncategorisedTasks.length > 0) {
        html += `
        <div class="bg-white p-4 rounded-lg shadow kanban-column" data-column-id="uncategorised">
            <h3 class="font-bold text-lg mb-4 text-gray-800 flex items-center capitalize">
                <span class="w-3 h-3 rounded-full mr-2" style="background-color: #ccc;"></span>
                Uncategorised
                <span class="text-sm font-normal text-gray-500 ml-2">${uncategorisedTasks.length}</span>
            </h3>
            <div class="space-y-3">
                ${uncategorisedTasks.map(task => createTaskCard(task)).join('')}
            </div>
        </div>`;
    }

    const numCols = categories.length + (uncategorisedTasks.length > 0 ? 1 : 0);
    const gridClass = getGridColsClass(numCols);
    board.className = `grid grid-cols-1 ${gridClass} gap-6`;
    board.innerHTML = html;
}


// --- Patient Kanban Renderers ---

/**
 * Renders the Patient Kanban board grouped by status category (Fast Mode).
 * (This function was already correct)
 */
export function renderPatientKanbanByStatus(board) {
    if (!board) { console.error("renderPatientKanbanByStatus: Board element not provided."); return; }
    board.innerHTML = ''; // Clear

    const showClosed = state.showClosed || false;
    let categories = getSortedCategories('patient');
    if (!showClosed) {
        categories = categories.filter(c => c.isClosed !== true);
    }
     if (categories.length === 0) {
         board.innerHTML = '<p class="text-gray-500 col-span-full text-center py-8">No patient categories configured or available.</p>';
         board.className = 'grid grid-cols-1 gap-6';
         return;
    }

    const gridClass = getGridColsClass(categories.length);
    board.className = `grid grid-cols-1 ${gridClass} gap-6`;

    const patientsByCategory = {};
    categories.forEach(cat => patientsByCategory[cat.id] = []);

    const patientStatusList = appData?.config?.patientStatus || [];
    const patientsToDisplay = (appData?.patients || []).filter(p => showClosed || !isItemClosed(p));

    patientsToDisplay.forEach(patient => {
        const statusConfig = patientStatusList.find(s => s.id === patient.status);
        const categoryId = statusConfig?.category;
        if (categoryId && patientsByCategory[categoryId]) {
            patientsByCategory[categoryId].push(patient);
        } else {
             console.warn(`Patient ${patient.id} has status '${patient.status}' with missing/invalid category '${categoryId}'. Skipping.`);
        }
    });

    // --- Sort patients within each category ---
    const priorities = appData?.config?.priorities || [];
    const priorityOrderMap = new Map(priorities.map(p => [p.id, parseInt(p.order, 10) || 0]));
    const statusOrderMap = new Map(patientStatusList.map((s, index) => [s.id, s.order ?? index]));
    const getPriorityOrder = (id) => priorityOrderMap.get(id) ?? 999;
    const getStatusOrder = (id) => statusOrderMap.get(id) ?? 999;

    Object.values(patientsByCategory).forEach(patientArray => {
        patientArray.sort((a, b) => {
            const statusOrderA = getStatusOrder(a.status);
            const statusOrderB = getStatusOrder(b.status);
            if (statusOrderA !== statusOrderB) {return statusOrderA - statusOrderB;}

            const priorityOrderA = getPriorityOrder(a.priority);
            const priorityOrderB = getPriorityOrder(b.priority);
            if (priorityOrderA !== priorityOrderB) {return priorityOrderA - priorityOrderB;}

            const dateA = getKanbanSortDate(a, appData.tasks);
            const dateB = getKanbanSortDate(b, appData.tasks);
            const d1 = dateA ? new Date(dateA) : null;
            const d2 = dateB ? new Date(dateB) : null;
            if (!d1 && !d2) {return 0;}
            if (!d1) {return 1;}
            if (!d2) {return -1;}
            return d1 - d2;
        });
    });

    // --- Generate HTML ---
    board.innerHTML = categories.map(category => {
        const categoryPatients = patientsByCategory[category.id] || [];
        const headerColor = category.color || '#cccccc';
        return `
        <div class="bg-white p-4 rounded-lg shadow kanban-column" data-column-id="${category.id}">
            <h3 class="font-bold text-lg mb-4 text-gray-800 flex items-center capitalize">
                <span class="w-3 h-3 rounded-full mr-2" style="background-color: ${headerColor};"></span>
                ${category.name || 'Unknown Category'}
                <span class="text-sm font-normal text-gray-500 ml-2">${categoryPatients.length}</span>
            </h3>
            <div class="space-y-3">
                ${categoryPatients.map(patient => createPatientCard_Fast(patient)).join('')}
            </div>
        </div>`;
    }).join('');
}

/**
 * Renders the Patient Kanban board grouped by due date (Fast Mode).
 * @param {HTMLElement} board - The container element for the Kanban board.
 */
export function renderPatientKanbanByDate(board) {
    if (!board) { console.error("renderPatientKanbanByDate: Board element not provided."); return; }
    board.innerHTML = ''; // Clear

    const showClosed = state.showClosed || false;
    
    // --- FIX: Use getGridColsClass for responsive layout ---
    const numCols = showClosed ? 5 : 4;
    const gridClass = getGridColsClass(numCols);
    board.className = `grid grid-cols-1 ${gridClass} gap-6`;
    
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const { startOfWeek: thisWeekStart, endOfWeek: thisWeekEnd } = getWeekBoundaries(today);
    const nextWeekDate = new Date(today); nextWeekDate.setDate(today.getDate() + 7);
    const { startOfWeek: nextWeekStart, endOfWeek: nextWeekEnd } = getWeekBoundaries(nextWeekDate);

    const patientColumns = { overdue: [], thisWeek: [], nextWeek: [], later: [], closed: [] };
    const allPatients = appData?.patients || [];

    allPatients.forEach(p => {
    if (isItemClosed(p)) {
        if (showClosed) {patientColumns.closed.push(p);}
        return;
    }

        const sortDateStr = getKanbanSortDate(p, appData.tasks);
        if (!sortDateStr) {
            patientColumns.later.push(p);
            return;
        }
        try {
        const sortDate = new Date(sortDateStr);
            if (isNaN(sortDate.getTime())) {
                console.warn(`Invalid sort date for patient ${p.id}: ${sortDateStr}`);
                patientColumns.later.push(p);
                return;
            }
            sortDate.setHours(0,0,0,0);

            if (sortDate < today) {patientColumns.overdue.push(p);}
            else if (sortDate >= thisWeekStart && sortDate <= thisWeekEnd) {patientColumns.thisWeek.push(p);}
            else if (sortDate >= nextWeekStart && sortDate <= nextWeekEnd) {patientColumns.nextWeek.push(p);}
            else {patientColumns.later.push(p);}
        } catch (e) {
            console.error(`Error processing sort date for patient ${p.id} ('${sortDateStr}'):`, e);
            patientColumns.later.push(p);
        }
    });

    // --- Sort within columns ---
    const priorities = appData?.config?.priorities || [];
    const patientStatuses = appData?.config?.patientStatus || [];
    const priorityOrderMap = new Map(priorities.map(p => [p.id, parseInt(p.order, 10) || 0]));
    const statusOrderMap = new Map(patientStatuses.map((s, index) => [s.id, s.order ?? index]));
    const getPriorityOrder = (id) => priorityOrderMap.get(id) ?? 999;
    const getStatusOrder = (id) => statusOrderMap.get(id) ?? 999;

    Object.values(patientColumns).forEach(columnArray => {
        columnArray.sort((a, b) => {
            const statusOrderA = getStatusOrder(a.status);
            const statusOrderB = getStatusOrder(b.status);
            if (statusOrderA !== statusOrderB) {return statusOrderA - statusOrderB;}

            const priorityOrderA = getPriorityOrder(a.priority);
            const priorityOrderB = getPriorityOrder(b.priority);
            if (priorityOrderA !== priorityOrderB) {return priorityOrderA - priorityOrderB;}

            const dateA = getKanbanSortDate(a, appData.tasks);
            const dateB = getKanbanSortDate(b, appData.tasks);
            const d1 = dateA ? new Date(dateA) : null;
            const d2 = dateB ? new Date(dateB) : null;
            if (!d1 && !d2) {return 0;}
            if (!d1) {return 1;}
            if (!d2) {return -1;}
            return d1 - d2;
        });
    });

    // --- Generate HTML ---
    const columnTitles = {overdue: 'Overdue', thisWeek: 'Due This Week', nextWeek: 'Due Next Week', later: 'Due Later', closed: 'Closed'};
    const columnsToRender = Object.entries(columnTitles).filter(([key]) => {
        return key !== 'closed' || (key === 'closed' && showClosed);
    });

     board.innerHTML = columnsToRender.map(([key, title]) => `
         <div class="bg-white p-4 rounded-lg shadow kanban-column">
             <h3 class="font-bold text-lg mb-4 text-gray-800">${title} <span class="text-sm font-normal text-gray-500">${patientColumns[key].length}</span></h3>
             <div class="space-y-3">
                 ${patientColumns[key].map(patient => createPatientCard_Fast(patient)).join('')}
             </div>
         </div>`).join('');
}

// --- Project Kanban Renderers ---

/**
 * Renders the Project Kanban board grouped by status category (Fast Mode).
 * (This function was already correct)
 */
export function renderProjectKanbanByStatus(board) {
    if (!board) { console.error("renderProjectKanbanByStatus: Board element not provided."); return; }
    board.innerHTML = ''; // Clear

    const showClosed = state.showClosed || false;
    let categories = getSortedCategories('project');
    if (!showClosed) {
        categories = categories.filter(c => c.isClosed !== true);
    }
    if (categories.length === 0) {
        board.innerHTML = '<p class="text-gray-500 col-span-full text-center py-8">No project categories configured or available.</p>';
        board.className = 'grid grid-cols-1 gap-6';
        return;
    }

     const gridClass = getGridColsClass(categories.length);
     board.className = `grid grid-cols-1 ${gridClass} gap-6`;

     const projectsByCategory = {};
     categories.forEach(cat => projectsByCategory[cat.id] = []);

     const projectStatusList = appData?.config?.projectStatus || [];
     const projectsToDisplay = (appData?.projects || []).filter(p => showClosed || !isItemClosed(p));

     projectsToDisplay.forEach(project => {
         const statusConfig = projectStatusList.find(s => s.id === project.status);
         const categoryId = statusConfig?.category;
         if (categoryId && projectsByCategory[categoryId]) {
             projectsByCategory[categoryId].push(project);
         } else {
              console.warn(`Project ${project.id} has status '${project.status}' with missing/invalid category '${categoryId}'. Skipping.`);
         }
     });

    // --- Sort projects within categories ---
    const priorities = appData?.config?.priorities || [];
    const priorityOrderMap = new Map(priorities.map(p => [p.id, parseInt(p.order, 10) || 0]));
    const statusOrderMap = new Map(projectStatusList.map((s, index) => [s.id, s.order ?? index]));
    const getPriorityOrder = (id) => priorityOrderMap.get(id) ?? 999;
    const getStatusOrder = (id) => statusOrderMap.get(id) ?? 999;

    Object.values(projectsByCategory).forEach(projectArray => {
        projectArray.sort((a, b) => {
            const statusOrderA = getStatusOrder(a.status);
            const statusOrderB = getStatusOrder(b.status);
            if (statusOrderA !== statusOrderB) {return statusOrderA - statusOrderB;}

            const priorityOrderA = getPriorityOrder(a.priority);
            const priorityOrderB = getPriorityOrder(b.priority);
            if (priorityOrderA !== priorityOrderB) {return priorityOrderA - priorityOrderB;}

            const dateA = getKanbanSortDate(a, appData.tasks);
            const dateB = getKanbanSortDate(b, appData.tasks);
            const d1 = dateA ? new Date(dateA) : null;
            const d2 = dateB ? new Date(dateB) : null;
            if (!d1 && !d2) {return 0;}
            if (!d1) {return 1;}
            if (!d2) {return -1;}
            return d1 - d2;
        });
    });

    // --- Generate HTML ---
     board.innerHTML = categories.map(category => {
         const categoryProjects = projectsByCategory[category.id] || [];
         const headerColor = category.color || '#cccccc';
         return `
         <div class="bg-white p-4 rounded-lg shadow kanban-column" data-column-id="${category.id}">
             <h3 class="font-bold text-lg mb-4 text-gray-800 flex items-center capitalize">
                 <span class="w-3 h-3 rounded-full mr-2" style="background-color: ${headerColor};"></span>
                 ${category.name || 'Unknown Category'}
                 <span class="text-sm font-normal text-gray-500 ml-2">${categoryProjects.length}</span>
             </h3>
             <div class="space-y-3">
                 ${categoryProjects.map(project => createProjectCard_Fast(project)).join('')}
             </div>
         </div>`;
     }).join('');
}

/**
 * Renders the Project Kanban board grouped by due date (Fast Mode).
 * @param {HTMLElement} board - The container element for the Kanban board.
 */
export function renderProjectKanbanByDate(board) {
    if (!board) { console.error("renderProjectKanbanByDate: Board element not provided."); return; }
    board.innerHTML = ''; // Clear

    const showClosed = state.showClosed || false;
    
    // --- FIX: Use getGridColsClass for responsive layout ---
    const numCols = showClosed ? 5 : 4;
    const gridClass = getGridColsClass(numCols);
    board.className = `grid grid-cols-1 ${gridClass} gap-6`;
    
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const { startOfWeek: thisWeekStart, endOfWeek: thisWeekEnd } = getWeekBoundaries(today);
    const nextWeekDate = new Date(today); nextWeekDate.setDate(today.getDate() + 7);
    const { startOfWeek: nextWeekStart, endOfWeek: nextWeekEnd } = getWeekBoundaries(nextWeekDate);

    const projectColumns = { overdue: [], thisWeek: [], nextWeek: [], later: [], closed: [] };
    const allProjects = appData?.projects || [];

    allProjects.forEach(p => {
        if (isItemClosed(p)) {
            if (showClosed) {projectColumns.closed.push(p);}
            return;
        }

        const sortDateStr = getKanbanSortDate(p, appData.tasks);
         if (!sortDateStr) {
             projectColumns.later.push(p);
             return;
         }
          try {
            const sortDate = new Date(sortDateStr);
            if (isNaN(sortDate.getTime())) {
                console.warn(`Invalid sort date for project ${p.id}: ${sortDateStr}`);
                projectColumns.later.push(p);
                return;
            }
             sortDate.setHours(0,0,0,0);

             if (sortDate < today) {projectColumns.overdue.push(p);}
             else if (sortDate >= thisWeekStart && sortDate <= thisWeekEnd) {projectColumns.thisWeek.push(p);}
             else if (sortDate >= nextWeekStart && sortDate <= nextWeekEnd) {projectColumns.nextWeek.push(p);}
             else {projectColumns.later.push(p);}
         } catch (e) {
              console.error(`Error processing sort date for project ${p.id} ('${sortDateStr}'):`, e);
              projectColumns.later.push(p);
         }
    });

    // --- Sort within columns ---
    const priorities = appData?.config?.priorities || [];
    const projectStatuses = appData?.config?.projectStatus || [];
    const priorityOrderMap = new Map(priorities.map(p => [p.id, parseInt(p.order, 10) || 0]));
    const statusOrderMap = new Map(projectStatuses.map((s, index) => [s.id, s.order ?? index]));
    const getPriorityOrder = (id) => priorityOrderMap.get(id) ?? 999;
    const getStatusOrder = (id) => statusOrderMap.get(id) ?? 999;

    Object.values(projectColumns).forEach(columnArray => {
        columnArray.sort((a, b) => {
            const statusOrderA = getStatusOrder(a.status);
            const statusOrderB = getStatusOrder(b.status);
            if (statusOrderA !== statusOrderB) {return statusOrderA - statusOrderB;}

            const priorityOrderA = getPriorityOrder(a.priority);
            const priorityOrderB = getPriorityOrder(b.priority);
            if (priorityOrderA !== priorityOrderB) {return priorityOrderA - priorityOrderB;}

            const dateA = getKanbanSortDate(a, appData.tasks);
            const dateB = getKanbanSortDate(b, appData.tasks);
            const d1 = dateA ? new Date(dateA) : null;
            const d2 = dateB ? new Date(dateB) : null;
            if (!d1 && !d2) {return 0;}
            if (!d1) {return 1;}
            if (!d2) {return -1;}
            return d1 - d2;
        });
    });

    // --- Generate HTML ---
    const columnTitles = { overdue: 'Overdue', thisWeek: 'Due This Week', nextWeek: 'Due Next Week', later: 'Due Later', closed: 'Closed' };
    const columnsToRender = Object.entries(columnTitles).filter(([key]) => {
        return key !== 'closed' || (key === 'closed' && showClosed);
    });

    board.innerHTML = columnsToRender.map(([key, title]) => `
        <div class="bg-white p-4 rounded-lg shadow kanban-column">
            <h3 class="font-bold text-lg mb-4 text-gray-800">${title} <span class="text-sm font-normal text-gray-500">${projectColumns[key].length}</span></h3>
            <div class="space-y-3">
                ${projectColumns[key].map(project => createProjectCard_Fast(project)).join('')}
            </div>
        </div>`).join('');
}

// --- Asset Kanban Renderers ---

/**
 * Renders the Asset Kanban board grouped by status category (Fast Mode).
 * (This function was already correct)
 */
export function renderAssetKanbanByStatus(board) {
    if (!board) { console.error("renderAssetKanbanByStatus: Board element not provided."); return; }
    board.innerHTML = ''; // Clear

    const showClosed = state.showClosed || false;
    let categories = getSortedCategories('asset');
    if (!showClosed) {
        categories = categories.filter(c => c.isClosed !== true);
    }
     if (categories.length === 0) {
         board.innerHTML = '<p class="text-gray-500 col-span-full text-center py-8">No asset categories configured or available.</p>';
          board.className = 'grid grid-cols-1 gap-6';
         return;
    }

    const gridClass = getGridColsClass(categories.length);
    board.className = `grid grid-cols-1 ${gridClass} gap-6`;

    const assetsByCategory = {};
    categories.forEach(cat => assetsByCategory[cat.id] = []);

    const equipmentStatusList = appData?.config?.equipmentStatus || [];
    const assetsToDisplay = (appData?.assets || []).filter(a => showClosed || !isItemClosed(a));

    assetsToDisplay.forEach(asset => {
        const statusConfig = equipmentStatusList.find(s => s.id === asset.equipment_status);
        const categoryId = statusConfig?.category;
        if (categoryId && assetsByCategory[categoryId]) {
            assetsByCategory[categoryId].push(asset);
        } else {
             console.warn(`Asset ${asset.id} has status '${asset.equipment_status}' with missing/invalid category '${categoryId}'. Skipping.`);
        }
    });

    // --- Sort assets within categories ---
    const statusOrderMap = new Map(equipmentStatusList.map((s, index) => [s.id, s.order ?? index]));
    const getStatusOrder = (id) => statusOrderMap.get(id) ?? 999;

    Object.values(assetsByCategory).forEach(assetArray => {
        assetArray.sort((a, b) => {
            const statusOrderA = getStatusOrder(a.equipment_status);
            const statusOrderB = getStatusOrder(b.equipment_status);
            if (statusOrderA !== statusOrderB) {return statusOrderA - statusOrderB;}

            const dateA = getKanbanSortDate(a, appData.tasks);
            const dateB = getKanbanSortDate(b, appData.tasks);
            const d1 = dateA ? new Date(dateA) : null;
            const d2 = dateB ? new Date(dateB) : null;
            if (!d1 && !d2) {return 0;}
            if (!d1) {return 1;}
            if (!d2) {return -1;}
            return d1 - d2;
        });
    });

    // --- Generate HTML ---
    board.innerHTML = categories.map(category => {
        const categoryAssets = assetsByCategory[category.id] || [];
        const headerColor = category.color || '#cccccc';
        return `
        <div class="bg-white p-4 rounded-lg shadow kanban-column" data-column-id="${category.id}">
            <h3 class="font-bold text-lg mb-4 text-gray-800 flex items-center capitalize">
                <span class="w-3 h-3 rounded-full mr-2" style="background-color: ${headerColor};"></span>
                ${category.name || 'Unknown Category'}
                <span class="text-sm font-normal text-gray-500 ml-2">${categoryAssets.length}</span>
            </h3>
            <div class="space-y-3">
                ${categoryAssets.map(asset => createAssetCard_Fast(asset)).join('')}
            </div>
        </div>`;
    }).join('');
}

/**
 * Renders the Asset Kanban board grouped by date category (Overdue, This Month, etc.) (Fast Mode).
 * @param {HTMLElement} board - The container element for the Kanban board.
 */
export function renderAssetKanbanByDate(board) {
    if (!board) { console.error("renderAssetKanbanByDate: Board element not provided."); return; }
    board.innerHTML = ''; // Clear

    const showClosed = state.showClosed || false;
    
    // --- FIX: Use getGridColsClass for responsive layout ---
    const numCols = showClosed ? 5 : 4;
    const gridClass = getGridColsClass(numCols);
    board.className = `grid grid-cols-1 ${gridClass} gap-6`;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const { startOfMonth: thisMonthStart, endOfMonth: thisMonthEnd } = getMonthBoundaries(today);
    const nextMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const { startOfMonth: nextMonthStart, endOfMonth: nextMonthEnd } = getMonthBoundaries(nextMonthDate);

    const assetColumns = { overdue: [], thisMonth: [], nextMonth: [], later: [], closed: [] };
    const allAssets = appData?.assets || [];

    allAssets.forEach(a => {
        if (isItemClosed(a)) {
            if (showClosed) {assetColumns.closed.push(a);}
            return;
        }

        const displayDateStr = getAssetDisplayDate(a);
        if (!displayDateStr) {
            assetColumns.later.push(a);
            return;
        }
         try {
            const sortDate = new Date(displayDateStr);
            if (isNaN(sortDate.getTime())) {
                console.warn(`Invalid sort date for asset ${a.id}: ${displayDateStr}`);
                assetColumns.later.push(a);
                return;
            }
            sortDate.setHours(0, 0, 0, 0);

            if (sortDate < today) {assetColumns.overdue.push(a);}
            else if (sortDate >= thisMonthStart && sortDate <= thisMonthEnd) {assetColumns.thisMonth.push(a);}
            else if (sortDate >= nextMonthStart && sortDate <= nextMonthEnd) {assetColumns.nextMonth.push(a);}
            else {assetColumns.later.push(a);}
        } catch (e) {
            console.error(`Error processing sort date for asset ${a.id} ('${displayDateStr}'):`, e);
            assetColumns.later.push(a);
        }
    });

    // --- Sort within columns ---
    const equipmentStatusList = appData?.config?.equipmentStatus || [];
    const statusOrderMap = new Map(equipmentStatusList.map((s, index) => [s.id, s.order ?? index]));
    const getStatusOrder = (id) => statusOrderMap.get(id) ?? 999;

    Object.values(assetColumns).forEach(columnArray => {
        columnArray.sort((a, b) => {
            const statusOrderA = getStatusOrder(a.equipment_status);
            const statusOrderB = getStatusOrder(b.equipment_status);
            if (statusOrderA !== statusOrderB) {return statusOrderA - statusOrderB;}

            const dateA = getKanbanSortDate(a, appData.tasks);
            const dateB = getKanbanSortDate(b, appData.tasks);
            const d1 = dateA ? new Date(dateA) : null;
            const d2 = dateB ? new Date(dateB) : null;
            if (!d1 && !d2) {return 0;}
            if (!d1) {return 1;}
            if (!d2) {return -1;}
            return d1 - d2;
        });
    });

    // --- Generate HTML ---
    const columnTitles = {overdue: 'Overdue', thisMonth: 'Due This Month', nextMonth: 'Due Next Month', later: 'Due Later', closed: 'Closed'};
    const columnsToRender = Object.entries(columnTitles).filter(([key]) => {
        return key !== 'closed' || (key === 'closed' && showClosed);
    });

    board.innerHTML = columnsToRender.map(([key, title]) => `
        <div class="bg-white p-4 rounded-lg shadow kanban-column">
            <h3 class="font-bold text-lg mb-4 text-gray-800">${title} <span class="text-sm font-normal text-gray-500">${assetColumns[key].length}</span></h3>
            <div class="space-y-3">
                ${assetColumns[key].map(asset => createAssetCard_Fast(asset)).join('')}
            </div>
        </div>`).join('');
}
